import { useState, useEffect, useCallback } from "react";

// @mui material components
import Container from "@mui/material/Container";
import ImageList from "@mui/material/ImageList";
import ImageListItem from "@mui/material/ImageListItem";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Data
import photosData, { allPhotos, sections } from "pages/Home/sections/data/photosData";

function PhotoGrid() {
  const [open, setOpen] = useState(false);
  const [activePhoto, setActivePhoto] = useState(null);

  const handleOpen = (photo) => {
    setActivePhoto(photo);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setActivePhoto(null);
  };

  const handleNav = useCallback(
    (direction) => {
      const currentIndex = allPhotos.indexOf(activePhoto);
      const nextIndex = (currentIndex + direction + allPhotos.length) % allPhotos.length;
      setActivePhoto(allPhotos[nextIndex]);
    },
    [activePhoto]
  );

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") handleNav(-1);
      else if (e.key === "ArrowRight") handleNav(1);
      else if (e.key === "Escape") handleClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, handleNav]);

  return (
    <MKBox component="section" py={6}>
      <Container>
        <MKTypography variant="body2" color="text" mb={4}>
          {allPhotos.length} photos &middot; Click any photo to view full size
        </MKTypography>

        {sections.map((sectionKey, idx) => {
          const section = photosData[sectionKey];
          return (
            <MKBox key={sectionKey} mb={6}>
              {idx > 0 && <Divider sx={{ mb: 4 }} />}
              <MKTypography variant="h4" mb={0.5}>
                {section.title}
              </MKTypography>
              <MKTypography variant="body2" color="text" mb={3}>
                {section.description}
              </MKTypography>
              <ImageList variant="masonry" cols={3} gap={12}>
                {section.photos.map((photo) => (
                  <ImageListItem
                    key={photo.alt}
                    sx={{
                      cursor: "pointer",
                      "&:hover": { opacity: 0.85 },
                      transition: "opacity 0.2s",
                    }}
                    onClick={() => handleOpen(photo)}
                  >
                    <MKBox
                      component="img"
                      src={photo.src}
                      alt={photo.alt}
                      loading="lazy"
                      width="100%"
                      borderRadius="lg"
                      shadow="md"
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            </MKBox>
          );
        })}
      </Container>

      {/* Lightbox Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullScreen
        PaperProps={{ sx: { bgcolor: "rgba(0,0,0,0.95)" } }}
      >
        {activePhoto && (
          <MKBox display="flex" flexDirection="column" height="100%">
            <IconButton
              onClick={handleClose}
              sx={{ position: "absolute", top: 8, right: 8, color: "#fff !important", zIndex: 2 }}
            >
              <Icon>close</Icon>
            </IconButton>
            <MKBox
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
              overflow="hidden"
              px={{ xs: 5, md: 8 }}
              py={2}
            >
              <IconButton
                onClick={() => handleNav(-1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: { xs: 0, md: 8 },
                  transform: "translateY(-50%)",
                  color: "#fff !important",
                  zIndex: 2,
                }}
              >
                <Icon sx={{ fontSize: "48px !important" }}>chevron_left</Icon>
              </IconButton>
              <IconButton
                onClick={() => handleNav(1)}
                sx={{
                  position: "absolute",
                  top: "50%",
                  right: { xs: 0, md: 8 },
                  transform: "translateY(-50%)",
                  color: "#fff !important",
                  zIndex: 2,
                }}
              >
                <Icon sx={{ fontSize: "48px !important" }}>chevron_right</Icon>
              </IconButton>
              <MKBox
                component="img"
                src={activePhoto.src}
                alt={activePhoto.alt}
                sx={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  display: "block",
                }}
              />
            </MKBox>
            <MKBox px={3} py={1.5} textAlign="center">
              <MKTypography variant="body2" color="white" fontWeight="bold">
                {activePhoto.alt}
              </MKTypography>
              {activePhoto.description && (
                <MKTypography variant="body2" sx={{ color: "rgba(255,255,255,0.7)" }} mt={0.5}>
                  {activePhoto.description}
                </MKTypography>
              )}
            </MKBox>
          </MKBox>
        )}
      </Dialog>
    </MKBox>
  );
}

export default PhotoGrid;
